import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "../css/PobDetailsDoctorChemist.css";
import * as XLSX from "xlsx";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "../pages/CustomButton";
const PobDetailsDoctorChemist = () => {
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const month = queryParams.get("month") || new Date().getMonth() + 1;
  const year = queryParams.get("year") || new Date().getFullYear();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/combined-retailers/pob-details-doctor-chemist`,
          {
            params: {
              employee_id: employeeId,
              month: month,
              year: year,
            },
            headers: getAuthHeaders(),
          }
        );
        setData(res.data || []);
      } catch (err) {
        setError("Failed to fetch POB details.");
        console.error(err);
      }
    };

    if (employeeId) {
      fetchData();
    } else {
      setError("Invalid or missing employee ID.");
    }
  }, [employeeId, month, year]);

  // --- GROUPING LOGIC: combine rows with same code into single row
  // For each unique code we: keep type/retailer/state/city/area/mobile/address from first occurrence,
  // join working_date values into one string, and sum pob into a single number.
  const groupedData = (() => {
    if (!Array.isArray(data) || data.length === 0) return [];

    const map = data.reduce((acc, item) => {
      const key = String(item.code); // normalize key to string
      if (!acc[key]) {
        acc[key] = {
          type: item.type,
          code: item.code,
          retailer: item.retailer,
          working_dates: item.working_date ? [item.working_date] : [],
          state: item.state,
          city: item.city,
          area: item.area,
          mobile: item.mobile,
          address: item.address ?? "",
          pob_total: Number(item.pob) || 0,
        };
      } else {
        // append date if present and not duplicate
        if (item.working_date && !acc[key].working_dates.includes(item.working_date)) {
          acc[key].working_dates.push(item.working_date);
        }
        acc[key].pob_total += Number(item.pob) || 0;
      }
      return acc;
    }, {});

    // convert map to array and format working_date and pob
    return Object.values(map).map((row) => ({
      type: row.type,
      code: row.code,
      retailer: row.retailer,
      working_date: row.working_dates.join(", "),
      state: row.state,
      city: row.city,
      area: row.area,
      mobile: row.mobile,
      address: row.address,
      pob: row.pob_total,
    }));
  })();
  // --- end grouping logic

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      groupedData.map((row, index) => ({
        "S.No": index + 1,
        Type: row.type,
        Code: row.code,
        Retailer: row.retailer,
        "Working Date": row.working_date,
        State: row.state,
        City: row.city,
        Area: row.area,
        Mobile: row.mobile,
        Address: row.address,
        POB: row.pob,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "POB Details");
    XLSX.writeFile(workbook, "POB_Details_Doctor_Chemist.xlsx");
  };



  return (
    <div className="pob-details-main">
      <div className="pob-details-data-container">
        <div className="pob-details-container">
          <h2 className="font-bold"><span className="text-xl">
            Selected Employee (Employee ID:- {employeeId})
         </span></h2>
        </div>
        {error && <p className="error-text">{error}</p>}

        <div className="border rounded shadow  table-wrapper">
        <table className="unlock-dcr-leave-table text-sm ">
          <thead className="bg-blue-500 text-white">
              <tr>
                <th>S.No</th>
                <th>Type</th>
                <th>Code</th>
                <th>Retailer</th>
                <th>Working Date</th>
                <th>State</th>
                <th>City</th>
                <th>Area</th>
                <th>Mobile</th>
                <th>Address</th>
                <th>POB</th>
              </tr>
            </thead>
            <tbody>
              {groupedData.length > 0 ? (
                groupedData.map((item, index) => (
                  <tr key={`${item.code}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{item.type}</td>
                    <td>{item.code}</td>
                    <td>{item.retailer}</td>
                    <td>{item.working_date}</td>
                    <td>{item.state}</td>
                    <td>{item.city}</td>
                    <td>{item.area}</td>
                    <td>{item.mobile}</td>
                    <td>{item.address}</td>
                    <td>{Number(item.pob).toFixed(2)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="no-data">
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="mt-4 flex gap-4 justify-center">
              <CustomButton onClick={handlePrint} label="Print" variant="print">
                🖨
              </CustomButton>
              <CustomButton
                onClick={handleExport}
                label="Export"
                variant="export"
              >
                📥
              </CustomButton>
            </div>

        </div>
      </div>
    </div>
  );
};

export default PobDetailsDoctorChemist;
